/**
 * InviteSetterForm Component
 *
 * Form component for gym owners to invite new setters via email.
 * Includes email validation, loading states, and success/error feedback.
 */

import { useState, type FormEvent, type CSSProperties } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';

/**
 * Props for InviteSetterForm component
 */
interface InviteSetterFormProps {
  /** ID of the gym to invite setter to */
  gymId: string;
  /** Callback when invitation is successfully sent */
  onSuccess?: (email: string) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Email validation pattern
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Form for inviting setters to a gym
 */
export const InviteSetterForm = ({ gymId, onSuccess, onError }: InviteSetterFormProps) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successEmail, setSuccessEmail] = useState('');

  /**
   * Validate email format
   */
  const isValidEmail = (value: string): boolean => {
    return EMAIL_PATTERN.test(value);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedEmail = email.trim().toLowerCase();

    // Validate email
    if (!trimmedEmail) {
      setError('Email is required');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const functions = getFirebaseFunctions();
      const inviteSetter = httpsCallable<
        { gymId: string; email: string },
        { success: boolean; invitationId: string }
      >(functions, 'inviteSetter');

      await inviteSetter({ gymId, email: trimmedEmail });

      setSuccess(true);
      setSuccessEmail(trimmedEmail);
      setEmail('');
      onSuccess?.(trimmedEmail);
    } catch (err) {
      const error = err as { code?: string; message?: string };
      let errorMessage = 'Failed to send invitation. Please try again.';

      // Handle specific error codes
      if (error.code === 'functions/already-exists') {
        errorMessage = 'A pending invitation already exists for this email.';
      } else if (error.code === 'functions/failed-precondition') {
        errorMessage = 'This user is already a setter at this gym.';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to invite setters.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      onError?.(new Error(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset success state to invite another setter
   */
  const handleInviteAnother = () => {
    setSuccess(false);
    setSuccessEmail('');
  };

  // Show success message after sending
  if (success) {
    return (
      <div style={successContainerStyle}>
        <div style={successHeaderStyle}>
          <span style={successIconStyle}>{'\u2713'}</span>
          <div>
            <p style={successTitleStyle}>Invitation sent!</p>
            <p style={successTextStyle}>
              An invitation has been sent to <strong>{successEmail}</strong>
            </p>
          </div>
        </div>
        <button type="button" onClick={handleInviteAnother} style={inviteAnotherButtonStyle}>
          Invite another setter
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={formGroupStyle}>
        <label htmlFor="setter-email" style={labelStyle}>
          Email Address
        </label>
        <input
          id="setter-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          placeholder="setter@example.com"
          disabled={loading}
          autoComplete="email"
          style={{
            ...inputStyle,
            ...(error ? inputErrorStyle : {}),
            ...(loading ? inputDisabledStyle : {}),
          }}
        />
        {error && <p style={errorTextStyle}>{error}</p>}
      </div>

      <button
        type="submit"
        disabled={loading || !email.trim()}
        style={{
          ...submitButtonStyle,
          ...(loading || !email.trim() ? submitButtonDisabledStyle : {}),
        }}
      >
        {loading ? (
          <>
            <span style={spinnerStyle} />
            Sending...
          </>
        ) : (
          'Send Invitation'
        )}
      </button>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <p style={helpTextStyle}>
        The invitee will receive an email with a link to accept the invitation. The link expires in
        7 days.
      </p>
    </form>
  );
};

/**
 * Component styles using CSS variables for dark mode support
 */
const successContainerStyle: CSSProperties = {
  padding: '24px',
  backgroundColor: 'var(--color-badge-success-bg)',
  borderRadius: '8px',
  border: '1px solid var(--color-success)',
};

const successHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
};

const successIconStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  backgroundColor: 'var(--color-success)',
  borderRadius: '50%',
  color: 'white',
  fontSize: '16px',
};

const successTitleStyle: CSSProperties = {
  fontWeight: 600,
  color: 'var(--color-badge-success-text)',
  margin: 0,
};

const successTextStyle: CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-badge-success-text)',
  margin: '4px 0 0 0',
};

const inviteAnotherButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-badge-success-text)',
  border: '1px solid var(--color-success)',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
};

const formGroupStyle: CSSProperties = {
  marginBottom: '16px',
};

const labelStyle: CSSProperties = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '16px',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-primary)',
};

const inputErrorStyle: CSSProperties = {
  border: '2px solid var(--color-error)',
};

const inputDisabledStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-tertiary)',
  opacity: 0.7,
};

const errorTextStyle: CSSProperties = {
  marginTop: '8px',
  fontSize: '14px',
  color: 'var(--color-error)',
};

const submitButtonStyle: CSSProperties = {
  width: '100%',
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};

const submitButtonDisabledStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
};

const spinnerStyle: CSSProperties = {
  display: 'inline-block',
  width: '16px',
  height: '16px',
  border: '2px solid white',
  borderTopColor: 'transparent',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

const helpTextStyle: CSSProperties = {
  marginTop: '12px',
  fontSize: '14px',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
};
